import React from 'react';
import { Link } from 'react-router-dom';
import { RoomIcon, normalizeRoomIconKey } from './roomIcons';
import './FarmRoomCard.css';

const SENSOR_FIELD_CONFIG = [
  {
    key: 'temperature',
    group: 'climate',
    aliases: ['temperature', 'temp'],
    labelKey: 'roomCard.labelTemperature',
    format: (value, formatNumber) => `${formatNumber(value)} °C`,
    stale: true,
  },
  {
    key: 'humidity',
    group: 'climate',
    aliases: ['humidity'],
    labelKey: 'roomCard.labelHumidity',
    format: (value, formatNumber) => `${formatNumber(value)} %`,
    stale: true,
  },
  {
    key: 'co2',
    group: 'climate',
    aliases: ['co2', 'CO2'],
    labelKey: 'roomCard.labelCo2',
    format: (value, formatNumber) => `${formatNumber(value, 0)} ppm`,
    stale: true,
  },
  {
    key: 'vpd',
    group: 'climate',
    aliases: ['vpd', 'VPD', 'vpdKpa', 'vpd_kpa'],
    labelKey: 'vpd',
    fallbackLabel: 'VPD',
    format: (value, formatNumber) => `${formatNumber(value, 2)} kPa`,
    stale: true,
  },
  {
    key: 'ph',
    group: 'water',
    aliases: ['ph', 'pH'],
    labelKey: 'roomCard.labelPh',
    fallbackLabel: 'pH',
    format: (value, formatNumber) => `${formatNumber(value, 2)}`,
  },
  {
    key: 'ec',
    group: 'water',
    aliases: ['ec', 'EC'],
    labelKey: 'roomCard.labelEc',
    fallbackLabel: 'EC',
    format: (value, formatNumber) => `${formatNumber(value, 2)}`,
  },
  {
    key: 'waterLevel',
    group: 'water',
    aliases: ['waterLevel', 'water_level', 'levelCm', 'level_cm', 'level'],
    labelKey: 'roomCard.labelWaterLevel',
    fallbackLabel: 'Water level',
    format: (value, formatNumber) => `${formatNumber(value, 1)}`,
  },
  {
    key: 'orp',
    group: 'water',
    aliases: ['orp', 'ORP'],
    labelKey: 'roomCard.labelOrp',
    fallbackLabel: 'ORP',
    format: (value, formatNumber) => `${formatNumber(value, 0)} mV`,
  },
];

function hasMetricValue(value) {
  return typeof value !== 'undefined' && value !== null && Number.isFinite(Number(value));
}

function getTranslatedLabel(t, labelKey, fallbackLabel) {
  const label = t(labelKey);
  return label !== labelKey ? label : fallbackLabel || labelKey;
}

function pickSensorValue(dataset, aliases) {
  for (const alias of aliases) {
    if (hasMetricValue(dataset?.[alias])) return Number(dataset[alias]);
  }
  return null;
}

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatEquipmentName(outlet = {}, labels = {}) {
  if (outlet?.equipment) return titleCase(outlet.equipment);
  if (outlet?.name) return titleCase(outlet.name);
  return labels.outletLabels?.[outlet?.key] || titleCase(outlet?.key || '');
}

function getControlModeLabel(outlet = {}, labels = {}) {
  const token = String(outlet.controlState || '').replace(/-/g, '_');
  if (token === 'manual_lock' || outlet.override) return labels.manualLock;
  if (token === 'temporary_manual_override') return labels.manualOverride;
  return labels.autoScenario;
}

function stateText(value, labels = {}) {
  if (value === true) return labels.on;
  if (value === false) return labels.off;
  return labels.unknown;
}

function normalizeAlertLevels(alertLevels) {
  if (!alertLevels) return {};
  if (alertLevels instanceof Map) return Object.fromEntries(alertLevels.entries());
  if (alertLevels instanceof Set) {
    return Object.fromEntries([...alertLevels].map((key) => [key, 'warning']));
  }
  return typeof alertLevels === 'object' ? alertLevels : {};
}

function addEquipmentRow(rows, row) {
  if (!row?.name) return;
  const key = row.key || row.name;
  if (rows.some((item) => item.key === key || item.name === row.name)) return;
  rows.push(row);
}

function buildEquipmentRows(room = {}, labels = {}) {
  const rows = [];
  const devices = Array.isArray(room.devices) ? room.devices : [];
  for (const device of devices) {
    const outlets = Array.isArray(device.control?.outlets) ? device.control.outlets : [];
    for (const outlet of outlets) {
      addEquipmentRow(rows, {
        key: outlet.key,
        name: formatEquipmentName(outlet, labels),
        state: outlet.state,
        mode: getControlModeLabel(outlet, labels),
      });
    }
  }
  const hasControlOutletRows = rows.length > 0;

  if (!hasControlOutletRows && room.hasClimateRelayData) {
    addEquipmentRow(rows, { key: 'ac', name: labels.outletLabels?.ac, state: Boolean(room.relayFan), mode: labels.autoScenario });
    addEquipmentRow(rows, { key: 'dryer', name: labels.outletLabels?.dryer, state: Boolean(room.relayDrop), mode: labels.autoScenario });
    addEquipmentRow(rows, { key: 'co2', name: labels.outletLabels?.co2, state: Boolean(room.relayLight), mode: labels.autoScenario });
    addEquipmentRow(rows, { key: 'extra', name: labels.outletLabels?.extra, state: Boolean(room.relayReserve), mode: labels.autoScenario });
  }

  if (!hasControlOutletRows && room.waterOutletOn !== undefined) {
    addEquipmentRow(rows, { key: 'pump', name: labels.outletLabels?.pump, state: Boolean(room.waterOutletOn), mode: labels.autoScenario });
  }

  return rows.filter((row) => row.name).slice(0, 4);
}

function ClimateValue({ valueText, subLabel, toneClass, t, isEmpty, flash, stale }) {
  return (
    <div
      className={
        'farm-room-card__clim-pill' +
        (flash && !stale ? ' farm-room-card__clim-pill--flash' : '')
      }
    >
      {isEmpty ? (
        <span className="farm-room-card__value farm-room-card__value--empty">{t('roomCard.noData')}</span>
      ) : (
        <span
          className={
            'farm-room-card__value' +
            (toneClass || '') +
            (stale ? ' farm-room-card__value--stale' : '')
          }
        >
          {valueText}
        </span>
      )}
      <span
        className={
          'farm-room-card__clim-sublabel' + (stale && !isEmpty ? ' farm-room-card__clim-sublabel--stale' : '')
        }
      >
        {subLabel}
      </span>
    </div>
  );
}

function MetricSection({ title, metrics, t, showFlash }) {
  if (!metrics.length) return null;
  const className =
    metrics.length <= 2
      ? 'farm-room-card__metrics farm-room-card__metrics--two'
      : 'farm-room-card__metrics farm-room-card__metrics--grid';
  return (
    <div className="farm-room-card__metric-section">
      <span className="farm-room-card__group-label">{title}</span>
      <div className={className} role="group" aria-label={title}>
        {metrics.map((metric) => (
          <ClimateValue
            key={metric.key}
            isEmpty={false}
            valueText={metric.valueText}
            subLabel={metric.subLabel}
            toneClass={metric.toneClass}
            t={t}
            flash={showFlash && metric.group === 'climate'}
            stale={metric.stale}
          />
        ))}
      </div>
    </div>
  );
}

function getDeviceId(device = {}, index = 0) {
  return String(device.deviceId || device.id || device._id || `device-${index}`);
}

function getDeviceLabel(device = {}, labels = {}) {
  return (
    device.displayName ||
    device.customName ||
    device.name ||
    labels.controllerLabels?.[device.controllerType || device.controller] ||
    device.deviceId ||
    device.id ||
    'Controller'
  );
}

function getDeviceChipClass(device = {}) {
  const type = String(device.controllerType || device.controller || device.type || 'sensor').toLowerCase();
  if (type.includes('climate')) return 'farm-room-card__device-chip--climate';
  if (type.includes('water')) return 'farm-room-card__device-chip--water';
  if (type.includes('relay')) return 'farm-room-card__device-chip--relay';
  return 'farm-room-card__device-chip--sensor';
}

/**
 * @param {object} props
 * @param {object} props.room
 * @param {function} props.t
 * @param {function} props.formatNumber
 * @param {string} props.toPath
 * @param {() => void} [props.onManage]
 * @param {boolean} [props.liveFlash]
 * @param {boolean} [props.climateStale]
 */
export default function FarmRoomCard({
  room,
  t,
  formatNumber,
  toPath,
  onManage,
  liveFlash,
  climateStale,
  alertCount = 0,
  alertLevels,
  lastUpdateText,
  labels = {},
}) {
  const typeKey = room.typeKey;
  const typeLine =
    typeKey && t(`roomType.${typeKey}`) !== `roomType.${typeKey}`
      ? t(`roomType.${typeKey}`)
      : room.type || (typeKey ? t(`roomType.${typeKey}`) : '');

  const online = room.onlineCount ?? 0;
  const offline = room.offlineCount ?? 0;
  const iconKey = normalizeRoomIconKey(room.iconKey);
  const sensorDataset = room.sensorData && typeof room.sensorData === 'object' ? room.sensorData : {};
  const metricAlertLevels = normalizeAlertLevels(alertLevels);
  const devices = Array.isArray(room.devices) ? room.devices : [];
  const deviceCount = room.deviceCount ?? devices.length;

  // Render only metrics that actually exist (no placeholders).
  const visibleMetrics = SENSOR_FIELD_CONFIG.map((config) => {
    const value = pickSensorValue(sensorDataset, config.aliases);
    if (!hasMetricValue(value)) return null;
    const level = metricAlertLevels[config.key];
    return {
      key: config.key,
      group: config.group,
      valueText: config.format(value, formatNumber),
      subLabel: getTranslatedLabel(t, config.labelKey, config.fallbackLabel),
      toneClass:
        level === 'critical'
          ? ' farm-room-card__value--critical'
          : level
            ? ' farm-room-card__value--out'
            : '',
      stale: config.stale ? Boolean(climateStale) : false,
    };
  }).filter(Boolean);

  const climateMetrics = visibleMetrics.filter((metric) => metric.group === 'climate');
  const waterMetrics = visibleMetrics.filter((metric) => metric.group === 'water');
  const showFlash = Boolean(liveFlash) && !climateStale;
  const equipmentRows = buildEquipmentRows(room, labels);
  const dayNightValidation = room.dayNightValidation || {};
  const dayNightStatus = String(dayNightValidation.status || '').toUpperCase();
  const dayNightReason = dayNightValidation.reason || '';
  const hasDayNightMismatch = dayNightStatus === 'MISMATCH';
  const dayNightMessage =
    hasDayNightMismatch
      ? labels.dayNightMismatch || t('roomsPage.dayNightCycleMismatchWarning')
      : dayNightReason === 'no_climate_controller'
        ? labels.noClimateController || t('roomsPage.noClimateControllerAssigned')
        : ['stale_telemetry', 'missing_telemetry', 'unknown_light_state'].includes(dayNightReason)
          ? labels.lightStatusUnknown || t('roomsPage.lightStatusUnknown')
          : '';
  const connectionIsHealthy = deviceCount > 0 && online > 0 && offline === 0;
  const roomIsIdle = deviceCount === 0;
  const roomStatus =
    roomIsIdle
      ? labels.statusIdle || t('roomCard.noData')
      : alertCount > 0 || offline > 0
      ? labels.statusAttention || t('roomCard.statusAttention')
      : labels.statusGood || t('roomCard.statusGood');

  return (
    <article className={`farm-room-card w-100 h-100 position-relative${roomIsIdle ? ' farm-room-card--idle' : ''}${offline > 0 ? ' farm-room-card--attention' : ''}`} dir="auto">
      <Link
        to={toPath}
        className="farm-room-card__cover"
        tabIndex={0}
        aria-label={t('roomCard.openRoom', String(room.name || ''))}
      />

      <div className="farm-room-card__content">
        {onManage ? (
          <button
            type="button"
            className="farm-room-card__gear-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onManage();
            }}
            title={labels.settings || t('roomCard.configure')}
            aria-label={labels.settings || t('roomCard.configure')}
          >
            <i className="fas fa-ellipsis-h" aria-hidden="true" />
          </button>
        ) : null}

        <div className="farm-room-card__main">
          <div className="farm-room-card__head">
            <div className="farm-room-card__type-badge" title={typeLine || undefined} aria-hidden="true">
              <RoomIcon iconKey={iconKey} className="farm-room-card__type-icon" />
            </div>
            <div className="farm-room-card__head-text min-w-0">
              <h3 className="farm-room-card__title text-truncate">{room.name}</h3>
              {typeLine ? <p className="farm-room-card__subtitle text-truncate mb-0">{typeLine}</p> : null}
            </div>
            <span className={`farm-room-card__connection ${connectionIsHealthy ? 'farm-room-card__connection--online' : roomIsIdle ? 'farm-room-card__connection--idle' : 'farm-room-card__connection--offline'}`}>
              {roomIsIdle ? labels.deviceIdle || t('roomCard.noData') : connectionIsHealthy ? labels.deviceOnline || t('roomCard.unitOnline') : labels.deviceOffline || t('roomCard.unitOffline')}
            </span>
          </div>

          <div className="farm-room-card__quick-row" aria-live="polite">
            <span className={`farm-room-card__status ${roomIsIdle ? 'farm-room-card__status--idle' : alertCount > 0 || offline > 0 ? 'farm-room-card__status--attention' : 'farm-room-card__status--good'}`}>
              {roomStatus}
            </span>
            <p className="farm-room-card__devices-line mb-0">
              <span className="farm-room-card__devices-on">
                {online} {t('roomCard.unitOnline')}
              </span>
              <span className="farm-room-card__devices-sep"> / </span>
              <span className="farm-room-card__devices-off">
                {offline} {t('roomCard.unitOffline')}
              </span>
            </p>
            <span className="farm-room-card__alerts-count">
              {alertCount} {alertCount === 1 ? labels.alertSingular || t('roomCard.alertSingular') : labels.alertPlural || t('roomCard.alertPlural')}
            </span>
            <span className="farm-room-card__alerts-count">
              {Number(room.openTaskCount || 0)} {labels.tasksLabel || t('nav.tasks')}
            </span>
          </div>

          <div className="farm-room-card__sync-row">
            <i className="far fa-clock" aria-hidden="true" />
            <span>{lastUpdateText || room.lastUpdateText || labels.noDataYet || t('roomCard.noData')}</span>
          </div>

          {alertCount > 0 ? (
            <div className="farm-room-card__alert-strip" role="status">
              <i className="fas fa-triangle-exclamation" aria-hidden="true" />
              <span>
                {alertCount} {alertCount === 1 ? labels.alertSingular || t('roomCard.alertSingular') : labels.alertPlural || t('roomCard.alertPlural')}
              </span>
            </div>
          ) : null}

          {dayNightMessage ? (
            <div className={`farm-room-card__cycle-strip${hasDayNightMismatch ? ' farm-room-card__cycle-strip--warn' : ''}`} role="status">
              <i className={hasDayNightMismatch ? 'fas fa-triangle-exclamation' : 'fas fa-circle-info'} aria-hidden="true" />
              <span>{dayNightMessage}</span>
            </div>
          ) : null}

          {roomIsIdle ? (
            <div className="farm-room-card__empty-state">
              <i className="fas fa-plug-circle-xmark" aria-hidden="true" />
              <span>{labels.noDevices || labels.noConnectedDevices || t('roomCard.noData')}</span>
            </div>
          ) : null}

          <MetricSection title={labels.latestClimate || 'Climate'} metrics={climateMetrics} t={t} showFlash={showFlash} />
          <MetricSection title={labels.latestWater || 'Water'} metrics={waterMetrics} t={t} showFlash={false} />

          {devices.length ? (
            <div className="farm-room-card__devices">
              <span className="farm-room-card__group-label">{labels.connectedDevices || t('nav.devices')}</span>
              <div className="farm-room-card__device-list">
                {devices.slice(0, 6).map((device, index) => {
                  const isOnline = String(device.status || '').toLowerCase() !== 'offline';
                  return (
                    <span
                      key={getDeviceId(device, index)}
                      className={`farm-room-card__device-chip ${isOnline ? getDeviceChipClass(device) : 'farm-room-card__device-chip--offline'}`}
                      title={getDeviceLabel(device, labels)}
                    >
                      <i className={isOnline ? 'fas fa-microchip' : 'fas fa-wifi-slash'} aria-hidden="true" />
                      <span>{getDeviceLabel(device, labels)}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="farm-room-card__equipment">
            <span className="farm-room-card__group-label">
              {labels.activeEquipment || t('roomCard.equipment')}
            </span>
            {equipmentRows.length ? (
              <div className="farm-room-card__equipment-list">
                {equipmentRows.map((item) => (
                  <span
                    key={item.key || item.name}
                    className={`farm-room-card__equipment-chip ${
                      item.state === true
                        ? 'farm-room-card__equipment-chip--on'
                        : item.state === false
                          ? 'farm-room-card__equipment-chip--off'
                          : ''
                    }`}
                    title={item.mode || undefined}
                  >
                    <span>{item.name}</span>
                    <strong>{stateText(item.state, labels)}</strong>
                  </span>
                ))}
              </div>
            ) : (
              <span className="farm-room-card__equipment-empty">
                {labels.noEquipmentState || t('roomCard.noData')}
              </span>
            )}
          </div>
          <span className="farm-room-card__open-cta">
            {labels.openRoom || t('roomCard.openRoom', String(room.name || ''))}
          </span>
        </div>
      </div>
    </article>
  );
}
